import { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useDrag, useDrop } from 'react-dnd';
import {
    SCHEDULE_HOURS,
    courtEventsConflict,
    isScheduleRangeVisible,
    minutesToScheduleTime,
    snapScheduleMinutes,
    type ScheduleBoardEvent,
} from './scheduleUtils';

export type SchedulePreviewInfo = {
    show: boolean;
    start: number;
    duration: number;
    type: 'template' | 'event' | null;
};

const ScheduleEvent: React.FC<{
    event: ScheduleBoardEvent;
    onDelete: (eventId: number) => void;
    pixelsPerMinute: number;
    startTime: number;
    endTime: number;
    onDoubleClick: (event: ScheduleBoardEvent) => void;
    maxCourtCount: number;
}> = ({ event, onDelete, pixelsPerMinute, startTime, endTime, onDoubleClick, maxCourtCount }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'event',
        item: event,
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }));

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        Modal.confirm({
            title: 'Confirm Delete',
            content: `Are you sure you want to delete ${event.title}?`,
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => onDelete(event.id),
        });
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDoubleClick(event);
    };

    const isInValidTimeRange = isScheduleRangeVisible(event.start, event.end, startTime, endTime);
    const actualHeight = (event.end - event.start) * pixelsPerMinute;
    const minHeight = 50;
    const isShortEvent = actualHeight < minHeight;
    const displayHeight = isHovered && isShortEvent ? minHeight : actualHeight;

    return (
        <div
            ref={drag}
            className={`event ${!isInValidTimeRange ? 'out-of-range' : ''}`}
            style={{
                top: `${event.start * pixelsPerMinute}px`,
                height: `${displayHeight}px`,
                opacity: isDragging ? 0.5 : (isInValidTimeRange ? 1 : 0.5),
                zIndex: isHovered && isShortEvent ? 100 : 5,
                position: 'absolute',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="event-header">
                <span className="event-title">{event.title}</span>
                <div className="event-actions">
                    {maxCourtCount <= 3 && (
                        <span className="event-time-inline" style={{ marginRight: '8px', fontSize: '12px', color: '#666' }}>
                            {minutesToScheduleTime(event.start)} - {minutesToScheduleTime(event.end)} ({event.duration}min)
                        </span>
                    )}
                    <SettingOutlined
                        className="edit-btn"
                        onClick={handleEdit}
                        style={{ marginRight: '8px' }}
                    />
                    <button
                        className="delete-btn"
                        onClick={handleDelete}
                    >×</button>
                </div>
            </div>
            {maxCourtCount > 3 && (
                <>
                    <div className="event-time">
                        {minutesToScheduleTime(event.start)} - {minutesToScheduleTime(event.end)}
                    </div>
                    <div className="event-duration">
                        {event.duration} Minutes
                    </div>
                </>
            )}
        </div>
    );
};

const PreviewBox: React.FC<{
    start: number;
    duration: number;
    type: 'template' | 'event' | null;
    pixelsPerMinute: number;
}> = ({ start, duration, type, pixelsPerMinute }) => (
    <div
        className="preview-box"
        style={{
            top: `${start * pixelsPerMinute}px`,
            height: `${duration * pixelsPerMinute}px`,
            borderColor: type === 'template' ? '#4CAF50' : '#2196F3',
        }}
    >
        <div className="preview-time">
            {minutesToScheduleTime(start)} - {minutesToScheduleTime(start + duration)}
        </div>
    </div>
);

interface ScheduleCourtProps {
    courtId: string;
    title: string;
    events: ScheduleBoardEvent[];
    onEventUpdate: (event: ScheduleBoardEvent) => void;
    onEventDrop: (event: ScheduleBoardEvent) => void;
    highlight: boolean;
    pixelsPerMinute: number;
    activePreviewCourtId: string | null;
    setActivePreviewCourtId: (courtId: string | null) => void;
    previewInfo: SchedulePreviewInfo | null;
    setPreviewInfo: (info: SchedulePreviewInfo | null) => void;
    startTime: number;
    endTime: number;
    onEventDoubleClick: (event: ScheduleBoardEvent) => void;
    maxCourtCount: number;
}

const ScheduleCourt: React.FC<ScheduleCourtProps> = ({
    courtId,
    title,
    events,
    highlight,
    onEventUpdate,
    onEventDrop,
    pixelsPerMinute,
    activePreviewCourtId,
    setActivePreviewCourtId,
    previewInfo,
    setPreviewInfo,
    startTime,
    endTime,
    onEventDoubleClick,
    maxCourtCount,
}) => {
    const courtRef = useRef<HTMLDivElement>(null);

    const getRelativeCourtPosition = (clientY: number): number => {
        if (!courtRef.current) return 0;
        const rect = courtRef.current.getBoundingClientRect();
        return clientY - rect.top;
    };

    const [{ canDrop }, drop] = useDrop(() => ({
        accept: ['template', 'event'],
        hover: (item: any, monitor) => {
            const itemType = monitor.getItemType();
            const offset = monitor.getClientOffset();
            if (!courtRef.current || !offset) return;

            setActivePreviewCourtId(courtId);
            const minutesFromTop = getRelativeCourtPosition(offset.y) / pixelsPerMinute;
            const start = snapScheduleMinutes(minutesFromTop);
            const duration = itemType === 'template' ? item.duration : item.end - item.start;

            setPreviewInfo({
                show: true,
                start,
                duration,
                type: itemType as 'template' | 'event',
            });
        },
        drop: (item: any, monitor) => {
            const itemType = monitor.getItemType();
            const offset = monitor.getClientOffset();
            if (!courtRef.current || !offset) return;

            const minutesFromTop = getRelativeCourtPosition(offset.y) / pixelsPerMinute;
            const start = snapScheduleMinutes(minutesFromTop);
            const duration = itemType === 'template' ? item.duration : item.end - item.start;
            const end = start + duration;

            if (start < startTime || end > endTime) {
                return;
            }

            if (courtEventsConflict(events, item.id, start, end)) {
                return;
            }

            if (itemType === 'template') {
                onEventDrop({
                    ...item,
                    courtId,
                    start,
                    end,
                    duration,
                });
            } else {
                onEventUpdate({
                    ...item,
                    courtId,
                    start,
                    end,
                });
            }
        },
        collect: (monitor) => ({
            canDrop: !!monitor.canDrop(),
        }),
    }), [pixelsPerMinute, events, onEventDrop, onEventUpdate, startTime, endTime, courtId, setActivePreviewCourtId, setPreviewInfo]);

    useEffect(() => {
        if (!canDrop && activePreviewCourtId === courtId) {
            setActivePreviewCourtId(null);
            setPreviewInfo(null);
        }
    }, [canDrop, activePreviewCourtId, courtId, setActivePreviewCourtId, setPreviewInfo]);

    drop(courtRef);

    return (
        <div className="court-wrapper">
            <div className="court-title">{title}</div>
            <div
                ref={courtRef}
                className={`court ${highlight ? 'highlight' : ''}`}
                style={{
                    height: `${SCHEDULE_HOURS * 60 * pixelsPerMinute}px`,
                    backgroundSize: `100% ${60 * pixelsPerMinute}px`,
                }}
            >
                <div
                    className="valid-time-range"
                    style={{
                        position: 'absolute',
                        top: `${startTime * pixelsPerMinute}px`,
                        height: `${(endTime - startTime) * pixelsPerMinute}px`,
                        left: '0',
                        right: '0',
                        background: 'rgba(255, 255, 255, 0.8)',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />

                {Array.from({ length: SCHEDULE_HOURS + 1 }).map((_, i) => (
                    <div
                        key={i}
                        className="hour-line"
                        style={{
                            top: `${i * 60 * pixelsPerMinute}px`,
                            left: '0',
                            right: '0',
                            position: 'absolute',
                            height: '1px',
                            background: '#ddd',
                            zIndex: 2,
                        }}
                    />
                ))}

                {events.map(event => (
                    <ScheduleEvent
                        key={event.id}
                        event={event}
                        onDelete={() => onEventUpdate({ ...event, courtId: '' })}
                        pixelsPerMinute={pixelsPerMinute}
                        startTime={startTime}
                        endTime={endTime}
                        onDoubleClick={onEventDoubleClick}
                        maxCourtCount={maxCourtCount}
                    />
                ))}

                {previewInfo?.show && activePreviewCourtId === courtId && (
                    <PreviewBox
                        start={previewInfo.start}
                        duration={previewInfo.duration}
                        type={previewInfo.type}
                        pixelsPerMinute={pixelsPerMinute}
                    />
                )}
            </div>
        </div>
    );
};

export default ScheduleCourt;
